import { logger } from '@teko/log';
import redis, { RedisClient, RetryStrategyOptions } from 'redis';
import { promisify } from 'util';
import { IRedisGroupKey } from '../libs';

const redisGroupKey: IRedisGroupKey = {
  SELLER: 'SELLER',
  MASTER_CAT: 'MASTER_CAT',
  CRON_JOB: 'CRON_JOB',
  SELLER_CAT: 'SELLER_CAT',
  MAPPING_SELLER_CAT_PRODUCTS: 'MAPPING_SELLER_CAT_PRODUCTS',
  MENU_ITEMS: 'MENU_ITEMS',
  SITEMAP: 'SITEMAP',
  COLLECTIONS: 'COLLECTIONS',
  CATEGORIES: 'CATEGORIES',
  ARTICLES: 'ARTICLES',
  PRODUCTS_CATEGORY: 'PRODUCTS_CATEGORY',
  PRODUCTS_COLLECTION: 'PRODUCTS_COLLECTION',
  PRODUCTS: 'PRODUCTS',
  LANDING: 'LANDING',
  LAYOUT: 'LAYOUT',
  BREADCRUMB: 'BREADCRUMB',
  FEATURE_FLAG: 'FEATURE_FLAG',
  FIXED_PRODUCT_VARIANTS: 'FIXED_PRODUCT_VARIANTS',
};

export const REDIS_DEFAULT_TIMEOUT = 3000;
export const REDIS_DEFAULT_EXPIRED = 24 * 60 * 60; // 1 day

class RedisInstance {
  private static redisInstance: RedisInstance;
  private redisClient: RedisClient;

  private constructor() {
    this.redisClient = redis.createClient({
      host: process.env.REDIS_HOST,
      port: process.env.REDIS_PORT ? Number(process.env.REDIS_PORT) : 6379,
      db: process.env.REDIS_DB ? Number(process.env.REDIS_DB) : 0,
      retry_strategy: (options: RetryStrategyOptions) => {
        if (options.attempt > 4)
          return new Error('Redis server refused the connection');
        // reconnect after 1 minutes
        return 60000;
      },
    });
    this.redisClient.on('error', logger.error);
  }

  public static getInstance() {
    if (!this.redisInstance) {
      this.redisInstance = new RedisInstance();
    }
    return this.redisInstance;
  }

  private asyncFn(fn: Function) {
    return promisify(fn).bind(this.redisClient);
  }

  getInstance() {
    return this.redisClient;
  }

  computeKeyName(group: string, key: string) {
    if (!redisGroupKey[group as keyof IRedisGroupKey]) return null;
    return `${redisGroupKey[group as keyof IRedisGroupKey]}:${key}`;
  }

  // default redis key is computed as group:key
  setValue(
    group: string,
    key: string,
    value: string,
    timeout = REDIS_DEFAULT_TIMEOUT,
    expired?: number,
  ) {
    const computedKey = this.computeKeyName(group, key);
    if (!computedKey) return null;

    logger.info(`redis:set:${computedKey}`);

    const args = expired
      ? [computedKey, value, 'EX', expired]
      : [computedKey, value];
    return promiseTimeout(this.asyncFn(this.redisClient.set)(...args), timeout);
  }

  msetValue(
    group: string,
    keyValues: { [key: string]: string },
    timeout = REDIS_DEFAULT_TIMEOUT,
    expired?: number,
    logId?: string,
  ) {
    let args: Array<string> = [];
    Object.entries(keyValues).forEach(([key, value]) => {
      const computedKey = this.computeKeyName(group, key);
      if (computedKey) {
        args = [...args, computedKey, value];
      }
    });

    logger.info(
      `${logId ? `[${logId}] ` : ''}redis:mset:${args
        .map((arg, index) => index % 2 === 0 && arg)
        .filter((arg) => !!arg)}`,
    );

    if (expired) {
      args = [...args, 'EX', expired.toString()];
    }

    return promiseTimeout(
      this.asyncFn(this.redisClient.mset)(...args),
      timeout,
      logId,
    );
  }

  deleteValueByKeys(keys: string[], timeout = REDIS_DEFAULT_TIMEOUT) {
    if (keys.length < 1) return new Promise((resolve) => resolve(null));
    logger.info(`redis:del:${keys}`);
    return promiseTimeout(this.asyncFn(this.redisClient.del)(...keys), timeout);
  }

  getValue(
    group: string,
    key: string,
    timeout = REDIS_DEFAULT_TIMEOUT,
  ): Promise<string> | null {
    const computedKey = this.computeKeyName(group, key);
    if (!computedKey) return null;

    return promiseTimeout(
      this.asyncFn(this.redisClient.get)(computedKey),
      timeout,
    );
  }

  mgetValue(
    group: string,
    keys: string[],
    timeout = REDIS_DEFAULT_TIMEOUT,
  ): Promise<string[]> {
    const computedKeys = keys
      .map((key) => this.computeKeyName(group, key))
      .filter((key) => !!key);
    if (computedKeys.length < 1) return new Promise((resolve) => resolve([]));

    return promiseTimeout(
      this.asyncFn(this.redisClient.mget)(...computedKeys),
      timeout,
    );
  }

  hashSetValue(
    group: string,
    keyValue: { [key: string]: string },
    timeout = REDIS_DEFAULT_TIMEOUT,
  ) {
    if (!redisGroupKey[group as keyof IRedisGroupKey]) return null;

    logger.info(
      `redis:hmset:${
        redisGroupKey[group as keyof IRedisGroupKey]
      }:${Object.keys(keyValue).join(',')}`,
    );

    return promiseTimeout(
      this.asyncFn(this.redisClient.hmset)(
        redisGroupKey[group as keyof IRedisGroupKey],
        keyValue,
      ),
      timeout,
    );
  }

  hashGetValue(
    group: string,
    key: string,
    timeout = REDIS_DEFAULT_TIMEOUT,
  ): Promise<string> | null {
    if (!redisGroupKey[group as keyof IRedisGroupKey]) return null;

    return promiseTimeout(
      this.asyncFn(this.redisClient.hget)(
        redisGroupKey[group as keyof IRedisGroupKey],
        key,
      ),
      timeout,
    );
  }

  getKeysByPattern(pattern: string, timeout = REDIS_DEFAULT_TIMEOUT) {
    return promiseTimeout(
      this.asyncFn(this.redisClient.keys)(pattern),
      timeout,
    );
  }
}

/**
 * This function is only called in server
 */
const fromRedisCache = async <T = unknown>(
  groupKey: string,
  key: string,
  getData: () => Promise<T>,
  expiredTime = REDIS_DEFAULT_EXPIRED,
  noCache = false,
) => {
  const redisInstance = RedisInstance.getInstance();
  const fetchData = async () => {
    return getData().then((result) => {
      try {
        if (
          (Array.isArray(result) && result.length > 0) ||
          (!Array.isArray(result) && result)
        ) {
          redisInstance.setValue(
            groupKey,
            key,
            JSON.stringify(result),
            REDIS_DEFAULT_TIMEOUT,
            expiredTime,
          );
        }
      } finally {
        return result;
      }
    });
  };

  try {
    const cacheData = await redisInstance.getValue(groupKey, key);

    return cacheData && !noCache ? (JSON.parse(cacheData) as T) : fetchData();
  } catch (e) {
    logger.error(e);
    return fetchData();
  }
};
export { RedisInstance, fromRedisCache, redisGroupKey };

/**
 *
 * @param promise
 * @param timeout: in milliseconds
 */
export const promiseTimeout = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  promise: Promise<any>,
  timeoutMillis: number,
  logId?: string,
) => {
  let timeoutFn: NodeJS.Timeout;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timeoutFn = setTimeout(() => {
        const errorMessage = `${
          logId ? `[${logId}] ` : ''
        }Time limit exceeded ${timeoutMillis}ms`;
        logger.error(errorMessage);
        reject(new Error(errorMessage));
      }, timeoutMillis);
    }),
  ])
    .then((v) => {
      clearTimeout(timeoutFn);
      return v;
    })
    .catch((err) => {
      clearTimeout(timeoutFn);
      throw err;
    });
};
